import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { CheckCircle2, Rocket } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { submitBetaInterest } from '@/services/betaInterestApi'
import { validateBetaInterest, type BetaInterestErrors, type BetaInterestFormValues } from './betaInterestForm'

const SOURCE = 'home_strip'
const EMPTY_VALUES: BetaInterestFormValues = { name: '', email: '', whatsapp: '' }
const GENERIC_ERROR = 'No pudimos registrar tus datos. Intenta de nuevo en unos minutos.'

export default function BetaInterestPage() {
  const [values, setValues] = useState<BetaInterestFormValues>(EMPTY_VALUES)
  const [errors, setErrors] = useState<BetaInterestErrors>({})
  const [trap, setTrap] = useState('')

  const mutation = useMutation({
    mutationFn: () =>
      submitBetaInterest({
        name: values.name.trim(),
        email: values.email.trim(),
        whatsapp: values.whatsapp.trim() || undefined,
        source: SOURCE,
        website: trap,
      }),
  })

  const setField = (field: keyof BetaInterestFormValues) => (value: string) =>
    setValues((current) => ({ ...current, [field]: value }))

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    const found = validateBetaInterest(values)
    setErrors(found)
    if (Object.keys(found).length === 0) mutation.mutate()
  }

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-10">
      <div className="mx-auto w-full max-w-md">
        <Link to="/" className="mb-6 flex justify-center">
          <img src="/new/IridiaChat_Logo_Fondo_Claro_Transparente.png" alt="IridiaChat" className="h-9 w-auto" />
        </Link>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          {mutation.isSuccess ? (
            <div role="status" className="text-center">
              <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" aria-hidden="true" />
              <h1 className="mt-3 text-xl font-bold text-slate-900">¡Listo, tu lugar está reservado!</h1>
              <p className="mt-2 text-sm text-slate-600">
                Te escribiremos por correo cuando tu reserva esté activa. La fase beta empieza el 6 de noviembre.
              </p>
              <Link to="/" className="mt-6 inline-block text-sm font-semibold text-emerald-700 underline">
                Volver al inicio
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-5 flex items-center gap-2 text-emerald-700">
                <Rocket className="h-5 w-5" aria-hidden="true" />
                <span className="text-sm font-semibold">Fase beta · 6 de noviembre</span>
              </div>
              <h1 className="text-2xl font-bold text-slate-900">Reserva tu lugar</h1>
              <p className="mt-1 text-sm text-slate-600">Déjanos tus datos y te avisamos cuando abramos la beta.</p>

              <form onSubmit={handleSubmit} noValidate className="mt-6 space-y-4">
                <div>
                  <label htmlFor="beta-name" className="mb-1 block text-sm font-medium text-slate-800">
                    Tu nombre
                  </label>
                  <Input
                    id="beta-name"
                    autoComplete="name"
                    value={values.name}
                    onChange={(e) => setField('name')(e.target.value)}
                    aria-invalid={Boolean(errors.name)}
                    aria-describedby={errors.name ? 'beta-name-error' : undefined}
                  />
                  {errors.name && (
                    <p id="beta-name-error" className="mt-1 text-sm text-red-700">
                      {errors.name}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="beta-email" className="mb-1 block text-sm font-medium text-slate-800">
                    Correo
                  </label>
                  <Input
                    id="beta-email"
                    type="email"
                    autoComplete="email"
                    value={values.email}
                    onChange={(e) => setField('email')(e.target.value)}
                    aria-required="true"
                    aria-invalid={Boolean(errors.email)}
                    aria-describedby={errors.email ? 'beta-email-error' : 'beta-email-help'}
                  />
                  {errors.email ? (
                    <p id="beta-email-error" className="mt-1 text-sm text-red-700">
                      {errors.email}
                    </p>
                  ) : (
                    <p id="beta-email-help" className="mt-1 text-xs text-slate-500">
                      Te avisaremos por aquí cuando tu lugar esté activo.
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="beta-whatsapp" className="mb-1 block text-sm font-medium text-slate-800">
                    WhatsApp <span className="font-normal text-slate-500">(opcional)</span>
                  </label>
                  <Input
                    id="beta-whatsapp"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="0414 1234567"
                    value={values.whatsapp}
                    onChange={(e) => setField('whatsapp')(e.target.value)}
                    aria-invalid={Boolean(errors.whatsapp)}
                    aria-describedby={errors.whatsapp ? 'beta-whatsapp-error' : undefined}
                  />
                  {errors.whatsapp && (
                    <p id="beta-whatsapp-error" className="mt-1 text-sm text-red-700">
                      {errors.whatsapp}
                    </p>
                  )}
                </div>

                {/* Campo trampa: fuera de pantalla y del teclado; solo lo llenan los bots. */}
                <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
                  <label htmlFor="beta-website">No llenar este campo</label>
                  <input
                    id="beta-website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={trap}
                    onChange={(e) => setTrap(e.target.value)}
                  />
                </div>

                {mutation.isError && (
                  <p role="alert" className="text-sm text-red-700">
                    {GENERIC_ERROR}
                  </p>
                )}

                <Button
                  type="submit"
                  className="w-full bg-emerald-600 text-white hover:bg-emerald-700"
                  disabled={mutation.isPending}
                >
                  {mutation.isPending ? 'Enviando…' : 'Reservar mi lugar'}
                </Button>

                <p className="text-xs text-slate-500">
                  Solo usaremos tus datos para avisarte de tu reserva. Más en nuestra{' '}
                  <Link to="/politica-privacidad" className="underline">
                    política de privacidad
                  </Link>
                  .
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </main>
  )
}
